import axiosInstance from "./axiosInstance";
import {API_BASE_URL, API_ENDPOINTS, PYTHON_API_BASE_URL } from "./apiConstants";
import axios from "axios";
export const simulationService = {
  // Get storage account name
  getStorageAccountName: async (branchId, token) => {
    try {
      const url = `${PYTHON_API_BASE_URL}${API_ENDPOINTS.STORAGE_ACCOUNT}/?branch_id=${branchId}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-User-ID": sessionStorage.getItem("UserID") || "",
        },
      });
  
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get simulation list
  getList: async ({ customerName, accessToken }) => {
    try {
      if (!customerName) {
        throw new Error("storage_account is required to list simulations.");
      }
      let listUrl = `${API_BASE_URL}${API_ENDPOINTS.STORAGE_MANAGER}/list?storage_account=${customerName}`;
      const response = await fetch(listUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "x-input-storage-manager-type": "AzureStorageManager",
          "X-User-ID": sessionStorage.getItem("UserID") || "",
        },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(
          `Failed to fetch JSON data: ${response.status} ${response.statusText}\n${text}`
        );
      }
  
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  

  // Delete a simulation atomically by its date_time_stamp.
  // The backend's /storage_manager/delete_simulation_results endpoint
  // matches every blob whose name contains "-{date_time_stamp}." and
  // deletes them as a unit (timeseries CSV, daily-summary CSV, the
  // versioned per-simulation sysconfig, plus optional violation files).
  // Surfaces { message } from the backend on success; on 404 / 500
  // axios will reject and callers should read err.response.data.message.
  deleteSimulation: async ({ storageAccount, microgridName, dateTimeStamp }) => {
    if (!storageAccount || !microgridName) {
      throw new Error(
        "storage_account and microgrid_name are required to delete a simulation.",
      );
    }
    if (!dateTimeStamp) {
      throw new Error("date_time_stamp is required to delete a simulation.");
    }
    const params = new URLSearchParams({
      storage_account: storageAccount,
      microgrid_name: microgridName,
      date_time_stamp: dateTimeStamp,
    });
    const { data } = await axiosInstance.delete(
      `${API_ENDPOINTS.DELETE_SIMULATION_RESULTS}?${params}`,
      {
        headers: {
          "x-output-storage-manager-type":
            process.env.REACT_APP_UPLOAD_FILES_STORAGE_MANAGER_TYPE ||
            "AzureStorageManager",
        },
      },
    );
    return data;
  },

  updateSimulationName: async ({ storageAccount, microgridName, dateTimeStamp, simulationName }) => {
    if (!storageAccount || !microgridName || !dateTimeStamp) {
      throw new Error("storage_account, microgrid_name and date_time_stamp are required.");
    }
    if (typeof simulationName !== "string") {
      throw new Error("simulation_name must be a string.");
    }
    const params = new URLSearchParams({
      storage_account: storageAccount,
      microgrid_name: microgridName,
      date_time_stamp: dateTimeStamp,
    });
    const { data } = await axiosInstance.post(
      `${API_ENDPOINTS.UPDATE_SIMULATION_NAME}?${params}`,
      { simulation_name: simulationName },
      {
        headers: {
          "x-output-storage-manager-type":
            process.env.REACT_APP_UPLOAD_FILES_STORAGE_MANAGER_TYPE ||
            "AzureStorageManager",
        },
      },
    );
    return data;
  },

  // Get simulation data - optimized for performance
  getSimulationData: async (
    type,
    datasetName,
    baseUrl,
    storageAccount,
    simulationName,
    token,
  ) => {
    const startTime = performance.now();
   
    if (!storageAccount || !simulationName) {
      throw new Error("storage_account and microgrid_name are required to fetch simulation data.");
    }

    try {
      // Same logic as fetchResultsData but optimized
      const endpoint =
        type === "Timeseries" ? "results_csv" : "results_csv";
      const queryParams = new URLSearchParams({
        storage_account: storageAccount,
        microgrid_name: simulationName,
        dataset_name: datasetName,
      });

      const url = `${baseUrl}/storage_manager/${endpoint}?${queryParams}`;
    

      const requestStartTime = performance.now();

      // Use axiosInstance for better performance and built-in optimizations
      const response = await axiosInstance.get(url, {
        headers: {
          "x-input-storage-manager-type":
            process.env.REACT_APP_DOWNLOAD_FILES_STORAGE_TYPE,
          Accept: "text/plain, text/csv",
          Authorization: `Bearer ${token}`,
          //"Connection": "keep-alive",
        },
        // Performance optimizations
        timeout: 180000, // 3 minutes timeout for large files
        responseType: "text", // Explicitly request text
        transformResponse: [(data) => data], // Skip any default transformations
        decompress: true, // Ensure decompression
        maxContentLength: 100 * 1024 * 1024, // 100MB max
        maxBodyLength: 100 * 1024 * 1024, // 100MB max

        // Add progress monitoring
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            
          } else {
            
          }
        },
      });

      const requestEndTime = performance.now();
      const requestDuration = requestEndTime - requestStartTime;
      const totalDuration = requestEndTime - startTime;

     

      return response.data;
    } catch (error) {
      const errorTime = performance.now();
      const errorDuration = errorTime - startTime;
      console.error(
        ` Request failed after ${errorDuration.toFixed(2)}ms:`,
        error,
      );
      throw error;
    }
  },

  // Get timeseries data - convenience method
  getTimeseriesData: async (
    datasetName,
    baseUrl,
    storageAccount,
    simulationName,
    token,
  ) => {
    return await simulationService.getSimulationData(
      "Timeseries",
      datasetName,
      baseUrl,
      storageAccount,
      simulationName,
      token,
    );
  },

  // Get daily summary data - convenience method
  getDailySummaryData: async (
    datasetName,
    baseUrl,
    storageAccount,
    simulationName,
    token,
  ) => {
    return await simulationService.getSimulationData(
      "DailySummary",
      datasetName,
      baseUrl,
      storageAccount,
      simulationName,
      token,
    );
  },

  // Download simulation data
  downloadSimulationData: async (
    type,
    datasetName,
    storageAccount,
    simulationName,
  ) => {
    try {
      if (!storageAccount || !simulationName) {
        throw new Error("storage_account and microgrid_name are required to download simulation data.");
      }
      const endpoint =
        type === "Timeseries" ? "results_csv" : "results_csv";
      const queryParams = new URLSearchParams({
        dataset_name: datasetName,
        storage_account: storageAccount,
        microgrid_name: simulationName,
      });

      const response = await axiosInstance.get(
        `${API_ENDPOINTS.STORAGE_MANAGER}/${endpoint}?${queryParams}`,
        {
          responseType: "blob",
          headers: {
            "x-input-storage-manager-type":
              process.env.REACT_APP_DOWNLOAD_FILES_STORAGE_TYPE,
            Accept: "text/csv",
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Performance test method for debugging
  testPerformance: async (
    type,
    datasetName,
    baseUrl,
    storageAccount,
    simulationName,
    token,
  ) => {
  
    const startTime = performance.now();

    try {
      const result = await simulationService.getSimulationData(
        type,
        datasetName,
        baseUrl,
        storageAccount,
        simulationName,
        token,
      );
      const endTime = performance.now();
      const duration = endTime - startTime;

      

      return { duration, dataSize: result.length, result };
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      console.error(
        ` Performance test failed after ${duration.toFixed(2)}ms:`,
        error,
      );
      throw error;
    }
  },

  // Load simulation data with timeseries
  loadSimulationData: async (
    simulation,
    baseUrl,
    storageAccount,
    simulationName,
    token,
  ) => {
    if (!simulation?.timeseries?.name) {
      throw new Error("Invalid simulation data");
    }

    try {
      const csvText = await simulationService.getTimeseriesData(
        simulation.timeseries.name,
        baseUrl,
        storageAccount,
        simulationName,
        token,
      );

      if (!csvText || csvText.trim() === "") {
        throw new Error("Received empty CSV data from server.");
      }

      return csvText;
    } catch (error) {
      throw error;
    }
  },
};
